import React, { useState } from "react";
import {
	Box,
	Button,
	Divider,
	FormLabel,
	FormControl,
	Link,
	TextField,
	Typography,
	Stack,
	Alert,
} from "@mui/material";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import { loginUser } from "../../services/api";
import { Loading } from "../../components/common/Loading";
import { useTranslation } from "react-i18next";
import { resendVerificationEmail } from "../../services/api";
import CheckIcon from "@mui/icons-material/Check";
import { useAuth } from "../../contexts/AuthContext";

const Card = styled(MuiCard)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	alignSelf: "center",
	width: "100%",
	maxHeight: "90vh",
	padding: theme.spacing(4),
	gap: theme.spacing(2),
	margin: "auto",
	overflowY: "auto",
	[theme.breakpoints.up("sm")]: {
		maxWidth: "450px",
	},
	boxShadow:
		"hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
	...theme.applyStyles("dark", {
		boxShadow:
			"hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
	}),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
	height: "calc((1 - var(--template-frame-height, 0)) * 100dvh)",
	minHeight: "100%",
	padding: theme.spacing(2),
	[theme.breakpoints.up("sm")]: {
		padding: theme.spacing(4),
	},
	"&::before": {
		content: '""',
		display: "block",
		position: "absolute",
		zIndex: -1,
		inset: 0,
		backgroundImage:
			"radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
		backgroundRepeat: "no-repeat",
		...theme.applyStyles("dark", {
			backgroundImage:
				"radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
		}),
	},
}));

export default function Login() {
	const [emailErrorMessage, setEmailErrorMessage] = useState("");
	const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [resendLoading, setResendLoading] = useState(false);
	const [resendSuccessMessage, setResendSuccessMessage] = useState("");

	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const { login } = useAuth();
	const { t } = useTranslation();

	const validateInputs = () => {
		let isValid = true;

		if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
			setEmailErrorMessage(t("pleaseEnterValidEmail"));
			isValid = false;
		} else {
			setEmailErrorMessage("");
		}

		if (!password.trim() || password.length < 6) {
			setPasswordErrorMessage(t("passwordMustBeAtLeast6Characters"));
			isValid = false;
		} else {
			setPasswordErrorMessage("");
		}

		return isValid;
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (emailErrorMessage || passwordErrorMessage) {
			return;
		}
		setIsLoading(true);
		try {
			const response = await loginUser({ email, password });
			login(response.token);
			localStorage.setItem("userCurrency", response.user.userCurrency);

			setEmail("");
			setPassword("");
			window.location.href = "/dashboard";
		} catch (err) {
			console.log(err || "Login failed. Please try again.");
			setErrorMessage(err?.message || "Login failed. Please try again.");
			setIsLoading(false);
		}
	};
	const handleResend = async () => {
		if (!email) return;
		setResendLoading(true);
		setResendSuccessMessage("");
		try {
			const data = await resendVerificationEmail(email);
			setResendSuccessMessage(data.message);
		} catch (err) {
			console.error(err);
		} finally {
			setResendLoading(false);
		}
	};

	return (
		<>
			{resendSuccessMessage && (
				<Box
					sx={{
						position: "fixed",
						top: "100px",
						left: "50%",
						transform: "translateX(-50%)",
						zIndex: 5,
						width: "auto",
					}}
				>
					<Alert
						icon={<CheckIcon fontSize="inherit" />}
						severity="success"
					>
						{resendSuccessMessage}
					</Alert>
				</Box>
			)}
			<SignInContainer direction="column" justifyContent="space-between">
				<Card variant="outlined">
					{isLoading && <Loading />}
					<Typography
						component="h1"
						variant="h4"
						sx={{
							width: "100%",
							fontSize: "clamp(2rem, 10vw, 2.15rem)",
						}}
					>
						{t("signIn")}
					</Typography>
					<Box
						component="form"
						onSubmit={handleSubmit}
						noValidate
						sx={{
							display: "flex",
							flexDirection: "column",
							width: "100%",
							gap: 1,
						}}
					>
						<FormControl>
							<FormLabel htmlFor="email">{t("email")}</FormLabel>
							<TextField
								error={emailErrorMessage !== ""}
								helperText={emailErrorMessage}
								type="email"
								placeholder="your@email.com"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
							/>
						</FormControl>
						<FormControl>
							<FormLabel htmlFor="password">
								{t("password")}
							</FormLabel>
							<TextField
								error={passwordErrorMessage !== ""}
								helperText={passwordErrorMessage}
								placeholder="•••••••••••"
								type="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
							/>
						</FormControl>
						<Button
							type="submit"
							fullWidth
							variant="contained"
							onClick={validateInputs}
							sx={{ mt: 1 }}
						>
							{t("signIn")}
						</Button>
					</Box>
					<Divider></Divider>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 2,
						}}
					>
						<Typography sx={{ textAlign: "center" }}>
							{t("dontHaveAnAccount")}{" "}
							<Link
								href="/register"
								variant="body2"
								sx={{ alignSelf: "center" }}
							>
								{t("signUp")}
							</Link>
						</Typography>
					</Box>
					{errorMessage ===
						"Please verify your email before logging in" && (
						<Typography sx={{ textAlign: "center" }}>
							Please verify your email before logging in. If you
							can't find the email, please use button below:
							<Button
								onClick={handleResend}
								variant="contained"
								disabled={resendLoading || !email}
								fullWidth
								sx={{ mt: 1 }}
							>
								{resendLoading
									? t("resendSending")
									: t("resendButton")}
							</Button>
						</Typography>
					)}
				</Card>
			</SignInContainer>
		</>
	);
}
